import React, { useRef, useState, useId } from 'react';
import { useTranslation } from 'react-i18next';

interface FileUploadProps {
  label: string;
  accept?: string;
  maxSizeMB?: number;
  currentFileUrl?: string | null;
  currentFileName?: string | null;
  onFileSelect: (file: File | null) => void;
  onRemove?: () => void;
  error?: string;
  hint?: string;
  disabled?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept = '.pdf,.jpg,.jpeg,.png',
  maxSizeMB = 10,
  currentFileUrl,
  currentFileName,
  onFileSelect,
  onRemove,
  error,
  hint,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Accessibility IDs
  const inputId = useId();
  const errorId = useId();
  const hintId = useId();
  const dropzoneId = useId();

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const validateFile = (file: File): boolean => {
    // Check size
    if (file.size > maxSizeBytes) {
      setLocalError(t('businessProfile.fileTooLarge', { maxSize: maxSizeMB }));
      return false;
    }

    // Check type
    const acceptedTypes = accept.split(',').map(t => t.trim().toLowerCase());
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const mimeType = file.type.toLowerCase();

    const isAccepted = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExtension === type;
      }
      if (type.includes('*')) {
        const [category] = type.split('/');
        return mimeType.startsWith(category + '/');
      }
      return mimeType === type;
    });

    if (!isAccepted) {
      setLocalError(t('businessProfile.invalidFileType'));
      return false;
    }

    setLocalError(null);
    return true;
  };

  const handleFileSelect = (file: File | null) => {
    if (file && !validateFile(file)) {
      return;
    }
    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const file = e.dataTransfer.files?.[0] || null;
    handleFileSelect(file);
  };

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setLocalError(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onFileSelect(null);
    if (onRemove) {
      onRemove();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const displayError = error || localError;
  const hasFile = selectedFile || currentFileUrl;
  const displayFileName = selectedFile?.name || currentFileName || t('businessProfile.currentFile');
  const hasError = Boolean(displayError);
  const hasHint = Boolean(hint) && !hasError;

  // Build aria-describedby based on what's present
  const describedBy = [
    hasError ? errorId : null,
    hasHint ? hintId : null,
  ].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className="block text-sm font-medium text-ink-primary">
        {label}
      </label>

      {/* Drop zone */}
      {!hasFile && (
        <div
          id={dropzoneId}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          aria-label={`${label}. ${t('businessProfile.clickToUpload')} ${t('businessProfile.orDragDrop')}`}
          aria-describedby={describedBy}
          aria-invalid={hasError}
          className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary/50 ${
            dragActive
              ? 'border-brand-primary bg-brand-primary/10'
              : displayError
              ? 'border-brand-warm bg-brand-warm/5'
              : 'border-border-strong hover:border-border-strong bg-surface-app-elevated'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept={accept}
            onChange={handleInputChange}
            disabled={disabled}
            aria-describedby={describedBy}
            className="sr-only"
          />

          <div className="flex flex-col items-center gap-2" aria-hidden="true">
            <svg
              className={`w-10 h-10 ${dragActive ? 'text-brand-primary' : 'text-ink-muted'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <div className="text-sm text-ink-secondary">
              <span className="text-brand-primary hover:text-brand-primary">
                {t('businessProfile.clickToUpload')}
              </span>{' '}
              {t('businessProfile.orDragDrop')}
            </div>
            <div className="text-xs text-ink-muted">
              {accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ')} •{' '}
              {t('businessProfile.maxSize', { size: maxSizeMB })}
            </div>
          </div>
        </div>
      )}

      {/* Selected file preview */}
      {hasFile && (
        <div className="flex items-center gap-3 p-3 bg-surface-app-elevated border border-border-strong rounded-lg">
          {/* File icon */}
          <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center">
            {selectedFile?.type.startsWith('image/') || currentFileUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
              <svg className="w-5 h-5 text-ink-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-ink-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            )}
          </div>

          {/* File info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {displayFileName}
            </p>
            <p className="text-xs text-ink-muted">
              {selectedFile
                ? formatFileSize(selectedFile.size)
                : t('businessProfile.existingFile')
              }
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {currentFileUrl && !selectedFile && (
              <a
                href={currentFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-ink-secondary hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary/50 rounded"
                aria-label={t('businessProfile.viewFile')}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </a>
            )}
            {!disabled && (
              <>
                <button
                  type="button"
                  onClick={handleClick}
                  className="p-2 text-ink-secondary hover:text-brand-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary/50 rounded"
                  aria-label={t('businessProfile.changeFile')}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="p-2 text-ink-secondary hover:text-brand-warm-deep transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary/50 rounded"
                  aria-label={t('businessProfile.removeFile')}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleInputChange}
            disabled={disabled}
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>
      )}

      {/* Hint */}
      {hasHint && (
        <p id={hintId} className="text-sm text-ink-muted">{hint}</p>
      )}

      {/* Error */}
      {hasError && (
        <p id={errorId} className="text-sm text-brand-warm-deep" role="alert">{displayError}</p>
      )}
    </div>
  );
};
